import React, { useState } from "react";
import axios from "axios";
import { Truck, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pincode / delivery serviceability checker used on product detail pages.
 * Hits `GET /api/shipping/quote` — the same backend logic that runs during
 * checkout — so what customers see here is what they will pay.
 */
const PincodeChecker = ({ subtotal = 0, className = "" }) => {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const check = async () => {
    setErr("");
    setResult(null);
    if (!/^\d{6}$/.test(pin)) {
      setErr("Enter a valid 6-digit pincode");
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ subtotal: String(subtotal || 0), pincode: pin });
      const r = await axios.get(`${API}/shipping/quote?${params.toString()}`);
      setResult({ serviceable: true, ...r.data });
      toast.success("Delivery available");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Pincode not serviceable";
      setResult({ serviceable: false });
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const eta = () => {
    // Use the configured "estimated_days" once returned by /shipping/quote (future);
    // meanwhile compute a friendly default window.
    const today = new Date();
    const a = new Date(today); a.setDate(a.getDate() + 3);
    const b = new Date(today); b.setDate(b.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    return `${fmt(a)} – ${fmt(b)}`;
  };

  return (
    <div className={"rounded-xl border border-[var(--hk-border)] bg-white/70 p-4 " + className} data-testid="pincode-checker">
      <div className="flex items-center gap-2 text-[13px] text-[var(--hk-ink)]">
        <Truck size={14} className="text-[var(--hk-orange-deep)]" />
        <span className="uppercase tracking-widest text-[11px] text-[var(--hk-ink-2)]">Delivery availability</span>
      </div>
      <div className="mt-3 flex items-stretch gap-2">
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter your pincode"
          className="flex-1 h-10 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[14px] focus:outline-none focus:border-[var(--hk-ink)]"
          data-testid="pincode-input"
          inputMode="numeric"
        />
        <button
          onClick={check}
          disabled={busy}
          className="hk-btn-primary h-10 px-4 !py-0 text-[13px]"
          data-testid="pincode-check-btn"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : "Check"}
        </button>
      </div>

      {err && (
        <p className="mt-2 text-[12px] text-red-700 flex items-center gap-1.5" data-testid="pincode-err">
          <X size={13} /> {err}
        </p>
      )}

      {result?.serviceable && (
        <div className="mt-3 text-[13px] text-[var(--hk-ink)]" data-testid="pincode-ok">
          <p className="flex items-center gap-1.5 text-emerald-700 font-medium"><Check size={13} /> Deliverable to <span className="font-mono">{pin}</span></p>
          <p className="mt-1 text-[var(--hk-ink-2)]">Shipping: <b>{result.total_shipping === 0 ? "Free" : `₹${(result.total_shipping / 100).toFixed(0)}`}</b> · Estimated arrival <b>{eta()}</b></p>
        </div>
      )}
    </div>
  );
};

export default PincodeChecker;
